/**
 * The two CBN exchange-rate series, shared by the premium Currency Analysis
 * page and the public Insights page so both describe them identically.
 *
 * They are stored under separate pairs in market.fx_rates and must never be
 * merged into one line. NFEM is the market-window rate the market actually
 * trades at and stays the headline everywhere. The official series is CBN's
 * published rate, daily back to 2001, and for most of that history it was
 * administered: it held flat for years at levels most buyers could not
 * obtain while the parallel market traded far higher. Only after the June
 * 2023 liberalisation do the two converge, tracking within about one percent
 * today. Both are refreshed by the same daily job.
 */

export const FX_SERIES = {
  nfem: {
    pair: 'NGN/USD',
    label: 'Market (NFEM)',
    short: 'NFEM',
    source: 'Source: CBN NFEM market window',
    blurb: 'The rate the market trades at. Our record starts when this feed was connected.',
  },
  official: {
    pair: 'NGN/USD-OFFICIAL',
    label: 'Official (CBN)',
    short: 'Official',
    source: 'Source: CBN published official rate',
    blurb: "CBN's published rate, daily back to 2001. Long-run context, not a trading rate.",
  },
};

/** Ranges worth offering only for the archive: NFEM cannot fill them. */
export const FX_LONG_RANGES = [
  { label: '5Y', value: 1827 },
  { label: '10Y', value: 3653 },
  { label: 'ALL', value: 99999 },
];

/** Liberalisation date: before this the official rate was administered. */
export const PEG_ERA_END = '2023-06-01';

/** True when the official series is showing and the window reaches the peg era. */
export const showsPegEra = (seriesKey, rows) =>
  seriesKey === 'official' && (rows?.[0]?.date || '9999') < PEG_ERA_END;

/**
 * Thin a long series for PLOTTING only (stats must read the full data).
 * The archive can hand us ~6,000 daily points, which is slow to draw as SVG
 * and adds no visible detail. The final point is always kept so the line ends
 * where the data does.
 */
export const thinForPlot = (rows, maxPoints = 900) => {
  const step = Math.ceil((rows?.length || 0) / maxPoints) || 1;
  if (step <= 1) return rows || [];
  return rows.filter((_, i) => i % step === 0 || i === rows.length - 1);
};

/**
 * Mark ranges the loaded series cannot fill, so a window that would show the
 * same few points under a bigger label is offered but disabled rather than
 * quietly lying about its span. A range stays enabled while the series holds
 * at least as much history as the next smaller range asks for, and ALL is
 * always enabled since it means "everything we have".
 *
 * Pass the FULL series, not the filtered view.
 */
export const withRangeAvailability = (rows, ranges) => {
  const first = rows?.[0]?.date;
  const last = rows?.[rows.length - 1]?.date;
  const spanDays =
    first && last ? Math.round((new Date(last) - new Date(first)) / 86400000) : 0;
  return ranges.map((r, i) => ({
    ...r,
    disabled: r.value < 99999 && spanDays < (i > 0 ? ranges[i - 1].value : 0),
  }));
};
